import { useState, useCallback, useEffect, useRef } from 'react';
import { useGlobalAPI } from '../../../../hooks/useGlobalAPI';

const useActivityFrequencyData = (activeFile) => {
  const api = useGlobalAPI(activeFile);

  // Data and loading states
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Metadata states
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [isMetadataLoaded, setIsMetadataLoaded] = useState(false);

  // Configuration states
  const [selectedObjectType, setSelectedObjectType] = useState("");
  const [selectedActivity, setSelectedActivity] = useState("");
  const [selectedTimeGranularity, setSelectedTimeGranularity] = useState("Month");
  const [selectedScope, setSelectedScope] = useState(10); // Default value
  const [configurationVisible, setConfigurationVisible] = useState(false);

  // Fetch metadata (object types and activities)
  // NOTE: This bypasses global filters to ensure metadata is always available
  // for component configuration, regardless of current filter state
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) {
      setError("No active file selected");
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Use a minimal request object without global filters
      const basicRequest = {
        file_type: activeFile
        // Intentionally omitting global filter fields for metadata requests
      };

      // Helper: try multiple endpoints to be robust against different API prefixes
      const tryPostEndpoints = async (endpoints, payload, label) => {
        const tried = [];
        for (const ep of endpoints) {
          try {
            // Use raw calls to bypass global filters entirely for metadata
            const res = await api.postRaw(ep, payload, {}, label);
            return { res, endpoint: ep };
          } catch (e) {
            tried.push({ endpoint: ep, message: e?.message || String(e) });
            // Continue trying next endpoint on 404/not found hints
            if (e?.message && /not found|404/i.test(e.message)) continue;
            // For other errors, rethrow immediately
            throw e;
          }
        }
        const details = tried.map(t => `${t.endpoint} → ${t.message}`).join(' | ');
        throw new Error(`All metadata endpoints failed: ${details}`);
      };

      // Fetch object types (without global filters) — try common paths
      const { res: objectTypesResponse } = await tryPostEndpoints(
        ['/filtered-object-types', '/ocel/filtered-object-types', '/v1/filtered-object-types'],
        basicRequest,
        'Object Types'
      );
      const types = objectTypesResponse.objectTypes || [];
      setObjectTypes(types);

      // Fetch object type activities (without global filters) — try POST first, then GET fallback
      let activitiesResponse;
      try {
        const { res } = await tryPostEndpoints(
          ['/object-type-activities', '/ocel/object-type-activities', '/v1/object-type-activities'],
          basicRequest,
          'Object Type Activities'
        );
        activitiesResponse = res;
      } catch (postErr) {
        // Fallback to GET simple variant if POST endpoints are unavailable
        try {
          activitiesResponse = await api.getRaw('/object-type-activities', { file_type: activeFile }, {}, 'Object Type Activities (GET)');
        } catch (getErr) {
          throw postErr; // surface original POST errors with attempted endpoints
        }
      }
      const activitiesMap = activitiesResponse.objectTypeActivities || {};
      setObjectTypeActivities(activitiesMap);

      setIsMetadataLoaded(true);
      setError(null);

      console.log(`✅ Metadata loaded: ${types.length} object types, activities for ${Object.keys(activitiesMap).length} object types`);
      return true;
    } catch (err) {
      console.error("Error fetching metadata:", err);
      setError(err.message || "Failed to load metadata");
      setIsMetadataLoaded(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeFile, api]); // Removed api from dependencies since we're using it directly

  // Fetch activity frequency data
  // NOTE: This bypasses global filters during data request to ensure we get results
  // regardless of current filter state. The activity frequency calculation will
  // still filter by object type and activity as configured.
  const fetchActivityFrequency = useCallback(async () => {
    if (!activeFile || !selectedObjectType || !selectedActivity) {
      setError("Missing required parameters: file, object type, or activity");
      return false;
    }
    // Latest-request guard to avoid stale updates
    const reqIdRef = fetchActivityFrequency._reqIdRef || (fetchActivityFrequency._reqIdRef = { current: 0 });
    reqIdRef.current += 1;
    const myId = reqIdRef.current;

    const markLoading = () => {
      if (myId === reqIdRef.current) setLoading(true);
      setError(null);
    };
    const finishLoading = () => {
      if (myId === reqIdRef.current) setLoading(false);
    };

    markLoading();

    // Minimal request object to bypass global filters
    const requestData = {
      file_type: activeFile,
      object_type: selectedObjectType,
      activity: selectedActivity,
      time_granularity: selectedTimeGranularity,
      scope: selectedScope
    };

    console.log("🔍 Fetching activity frequency data with configuration:", {
      ...requestData,
      note: "Global filters are bypassed - we filter by object type and activity only"
    });

    const tryPostEndpoints = async (endpoints, payload, label) => {
      const tried = [];
      for (const ep of endpoints) {
        try {
          const res = await api.postRaw(ep, payload, {}, label);
          return { res, endpoint: ep };
        } catch (e) {
          tried.push({ endpoint: ep, message: e?.message || String(e) });
          if (e?.message && /not found|404/i.test(e.message)) continue;
          throw e;
        }
      }
      const details = tried.map(t => `${t.endpoint} → ${t.message}`).join(' | ');
      throw new Error(`All activity-frequency endpoints failed: ${details}`);
    };

    const attemptFetch = async (allowRetry = true) => {
      try {
        const { res: responseData } = await tryPostEndpoints(
          ['/visualization/activity-frequency', '/activity-frequency', '/analytics/activity-frequency', '/v1/activity-frequency'],
          requestData,
          'Activity Frequency'
        );

        if (myId !== reqIdRef.current) {
          // Outdated response, ignore
          return false;
        }

        if (responseData && 'data' in responseData) {
          const dataPoints = responseData.data || [];
          setChartData(dataPoints);
          console.log(`✅ Activity frequency data loaded: ${dataPoints.length} data points`);
          console.log(`   Total events analyzed: ${responseData.total_events || 'unknown'}`);
          if (dataPoints.length > 0) {
            console.log("📊 Sample data points:", dataPoints.slice(0, 3));
          }
          finishLoading();
          return true;
        } else {
          console.error("❌ Invalid response format:", responseData);
          setChartData([]);
          setError("Server returned invalid response format");
          finishLoading();
          return false;
        }
      } catch (err) {
        console.error("❌ Error fetching activity frequency data:", err);

        const msg = err?.message || '';
        const is500 = /\b(500|Server error)\b/i.test(msg) || /processing request/i.test(msg);
        if (allowRetry && is500) {
          console.warn("Retrying once after transient server error...");
          await new Promise(r => setTimeout(r, 300));
          return await attemptFetch(false);
        }

        if (myId === reqIdRef.current) {
          let errorMessage = "Failed to load activity frequency data";
          if (msg.includes("404")) {
            errorMessage = "Data endpoint not found. Please check if the backend is running.";
          } else if (msg.includes("500") || /Server error/i.test(msg)) {
            errorMessage = "Server error processing request. Please check the backend logs.";
          } else if (msg.includes("400")) {
            errorMessage = "Invalid configuration parameters. Please check your selections.";
          } else if (msg) {
            errorMessage = `Server error: ${msg}`;
          }
          setError(errorMessage);
          setChartData([]);
          finishLoading();
        }
        return false;
      }
    };

    return await attemptFetch(true);
  }, [activeFile, selectedObjectType, selectedActivity, selectedTimeGranularity, selectedScope, api]);

  // Clear error when user changes configuration inputs
  useEffect(() => {
    if (error) {
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedObjectType, selectedActivity, selectedTimeGranularity, selectedScope]);

  // Reset data and metadata when activeFile changes
  useEffect(() => {
    setChartData([]);
    setError(null);
    setIsMetadataLoaded(false);
    setObjectTypes([]);
    setObjectTypeActivities({});
    setSelectedObjectType("");
    setSelectedActivity("");
    setConfigurationVisible(false);
  }, [activeFile]);

  return {
    // Data and fetch function
    chartData,
    loading,
    error,

    // Metadata
    objectTypes,
    objectTypeActivities,
    isMetadataLoaded,

    // Configuration state
    selectedObjectType,
    selectedActivity,
    selectedTimeGranularity,
    selectedScope,
    configurationVisible,

    // Setters
    setSelectedObjectType,
    setSelectedActivity,
    setSelectedTimeGranularity,
    setSelectedScope,
    setConfigurationVisible,

    // Actions
    fetchMetadata,
    fetchActivityFrequency,
  };
};

export { useActivityFrequencyData };
